import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Check, X, Sparkles, Zap, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_FEATURES = {
  free: {
    icon: Zap,
    color: "text-slate-600",
    bg: "bg-slate-50",
    border: "border-slate-200",
    badge: null,
    features: [
      { text: "5 AI questions per day", included: true },
      { text: "3 practice problems per day", included: true },
      { text: "Basic explanations", included: true },
      { text: "Text-to-Speech", included: false },
      { text: "Unlimited questions", included: false },
      { text: "Priority responses", included: false },
    ],
  },
  pro: {
    icon: Sparkles,
    color: "text-primary",
    bg: "bg-primary/5",
    border: "border-primary/30",
    badge: "Most Popular",
    features: [
      { text: "Unlimited AI questions", included: true },
      { text: "Unlimited practice problems", included: true },
      { text: "Step-by-step explanations", included: true },
      { text: "Text-to-Speech voice mode", included: true },
      { text: "Solution history", included: true },
      { text: "Priority responses", included: false },
    ],
  },
  premium: {
    icon: Crown,
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-200",
    badge: "Best Value",
    features: [
      { text: "Everything in Pro", included: true },
      { text: "Priority AI responses", included: true },
      { text: "Advanced explanations", included: true },
      { text: "Text-to-Speech voice mode", included: true },
      { text: "Unlimited everything", included: true },
      { text: "Early access to new features", included: true },
    ],
  },
};

const CYCLE_LABELS = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
};

const CYCLE_SAVINGS = {
  monthly: null,
  quarterly: "Save 17%",
  yearly: "Save 25%",
};

export default function PricingPage() {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [pricing, setPricing] = useState(null);
  const [currentSub, setCurrentSub] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null);
  const [polling, setPolling] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);

  // Load plans & subscription
  useEffect(() => {
    const load = async () => {
      try {
        const plansRes = await axios.get(`${API}/subscriptions/plans`);
        setPricing(plansRes.data.pricing);

        if (isAuthenticated) {
          const subRes = await axios.get(`${API}/subscriptions/status`);
          setCurrentSub(subRes.data);
        }
      } catch (err) {
        console.error("Failed to load plans:", err);
      }
    };
    load();
  }, [isAuthenticated]);

  // Poll checkout status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId || !isAuthenticated) return;

    let attempts = 0;
    const maxAttempts = 8;
    setPolling(true);

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/subscriptions/checkout/status/${sessionId}`);
        if (res.data.status === "paid") {
          toast.success("Payment successful! Your subscription is now active.");
          setPolling(false);
          // Refresh subscription
          const subRes = await axios.get(`${API}/subscriptions/status`);
          setCurrentSub(subRes.data);
          // Clean URL
          navigate("/pricing", { replace: true });
          return;
        }
      } catch (err) {
        console.error("Poll error:", err);
      }

      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        setPolling(false);
        toast.info("Payment is being processed. Check back shortly.");
        navigate("/pricing", { replace: true });
      }
    };

    poll();
  }, [searchParams, isAuthenticated, navigate]);

  const handleCheckout = async (plan) => {
    if (!isAuthenticated) {
      navigate("/auth");
      return;
    }
    setCheckingOut(plan);
    try {
      const res = await axios.post(`${API}/subscriptions/checkout`, {
        plan,
        billing_cycle: billingCycle,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start checkout");
      setCheckingOut(null);
    }
  };

  const handleStartTrial = async () => {
    if (!isAuthenticated) {
      navigate("/auth");
      return;
    }
    setTrialLoading(true);
    try {
      await axios.post(`${API}/subscriptions/start-trial`);
      toast.success("Free trial started! Enjoy Pro features for 5 days.");
      const subRes = await axios.get(`${API}/subscriptions/status`);
      setCurrentSub(subRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start trial");
    } finally {
      setTrialLoading(false);
    }
  };

  const activePlan = currentSub?.subscription?.plan || "free";
  const isTrial = currentSub?.subscription?.trial || false;

  if (authLoading || !pricing) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 page-transition" data-testid="pricing-page">
      {/* Polling overlay */}
      {polling && (
        <div className="fixed inset-0 z-50 bg-background/80 flex items-center justify-center" data-testid="payment-polling">
          <div className="text-center space-y-4">
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-primary" />
            <p className="text-lg font-medium">Verifying your payment...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] mb-3" data-testid="pricing-title">
          Choose Your Plan
        </h1>
        <p className="text-lg text-muted-foreground max-w-xl mx-auto">
          Unlock unlimited math tutoring with DeepCore Math AI
        </p>

        {/* Current plan badge */}
        {isAuthenticated && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-sm" data-testid="current-plan-badge">
            Current plan: <span className="font-semibold capitalize">{activePlan}</span>
            {isTrial && <span className="text-xs text-orange-600 font-medium">(Trial)</span>}
          </div>
        )}
      </div>

      {/* Billing Toggle */}
      <div className="flex justify-center mb-10" data-testid="billing-toggle">
        <div className="inline-flex bg-muted rounded-full p-1 gap-1">
          {Object.entries(CYCLE_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setBillingCycle(key)}
              data-testid={`billing-${key}`}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === key
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
              {CYCLE_SAVINGS[key] && (
                <span className="ml-1.5 text-xs text-green-600 font-semibold">{CYCLE_SAVINGS[key]}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Plan Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-12" data-testid="plan-cards">
        {/* Free Plan */}
        <Card className={`relative ${PLAN_FEATURES.free.border}`} data-testid="plan-free">
          <CardHeader className="pb-4">
            <div className={`w-10 h-10 rounded-xl ${PLAN_FEATURES.free.bg} flex items-center justify-center mb-3`}>
              <Zap className={`w-5 h-5 ${PLAN_FEATURES.free.color}`} />
            </div>
            <CardTitle className="font-['Outfit'] text-xl">Free</CardTitle>
            <div className="mt-2">
              <span className="text-3xl font-bold">$0</span>
              <span className="text-muted-foreground text-sm"> forever</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-3">
              {PLAN_FEATURES.free.features.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  {f.included ? (
                    <Check className="w-4 h-4 text-green-500 shrink-0" />
                  ) : (
                    <X className="w-4 h-4 text-slate-300 shrink-0" />
                  )}
                  <span className={f.included ? "" : "text-muted-foreground"}>{f.text}</span>
                </li>
              ))}
            </ul>
            {activePlan === "free" && !isTrial ? (
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={handleStartTrial}
                disabled={trialLoading}
                data-testid="start-trial-btn"
              >
                {trialLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Start 5-Day Free Trial
              </Button>
            ) : (
              <Button variant="outline" className="w-full rounded-full" disabled data-testid="current-plan-btn">
                {activePlan === "free" ? "Current Plan" : "Downgrade"}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Pro Plan */}
        <Card className={`relative ${PLAN_FEATURES.pro.border} ring-2 ring-primary/20`} data-testid="plan-pro">
          {PLAN_FEATURES.pro.badge && (
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
              {PLAN_FEATURES.pro.badge}
            </div>
          )}
          <CardHeader className="pb-4">
            <div className={`w-10 h-10 rounded-xl ${PLAN_FEATURES.pro.bg} flex items-center justify-center mb-3`}>
              <Sparkles className={`w-5 h-5 ${PLAN_FEATURES.pro.color}`} />
            </div>
            <CardTitle className="font-['Outfit'] text-xl">Pro</CardTitle>
            <div className="mt-2">
              <span className="text-3xl font-bold">${pricing.pro[billingCycle]}</span>
              <span className="text-muted-foreground text-sm"> /{billingCycle === "monthly" ? "mo" : billingCycle === "quarterly" ? "3 mo" : "yr"}</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-3">
              {PLAN_FEATURES.pro.features.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  {f.included ? (
                    <Check className="w-4 h-4 text-green-500 shrink-0" />
                  ) : (
                    <X className="w-4 h-4 text-slate-300 shrink-0" />
                  )}
                  <span className={f.included ? "" : "text-muted-foreground"}>{f.text}</span>
                </li>
              ))}
            </ul>
            <Button
              className="w-full rounded-full"
              onClick={() => handleCheckout("pro")}
              disabled={checkingOut === "pro" || activePlan === "pro"}
              data-testid="checkout-pro-btn"
            >
              {checkingOut === "pro" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {activePlan === "pro" ? "Current Plan" : "Upgrade to Pro"}
            </Button>
          </CardContent>
        </Card>

        {/* Premium Plan */}
        <Card className={`relative ${PLAN_FEATURES.premium.border}`} data-testid="plan-premium">
          {PLAN_FEATURES.premium.badge && (
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full">
              {PLAN_FEATURES.premium.badge}
            </div>
          )}
          <CardHeader className="pb-4">
            <div className={`w-10 h-10 rounded-xl ${PLAN_FEATURES.premium.bg} flex items-center justify-center mb-3`}>
              <Crown className={`w-5 h-5 ${PLAN_FEATURES.premium.color}`} />
            </div>
            <CardTitle className="font-['Outfit'] text-xl">Premium</CardTitle>
            <div className="mt-2">
              <span className="text-3xl font-bold">${pricing.premium[billingCycle]}</span>
              <span className="text-muted-foreground text-sm"> /{billingCycle === "monthly" ? "mo" : billingCycle === "quarterly" ? "3 mo" : "yr"}</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-3">
              {PLAN_FEATURES.premium.features.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  {f.included ? (
                    <Check className="w-4 h-4 text-green-500 shrink-0" />
                  ) : (
                    <X className="w-4 h-4 text-slate-300 shrink-0" />
                  )}
                  <span className={f.included ? "" : "text-muted-foreground"}>{f.text}</span>
                </li>
              ))}
            </ul>
            <Button
              className="w-full rounded-full bg-amber-500 hover:bg-amber-600"
              onClick={() => handleCheckout("premium")}
              disabled={checkingOut === "premium" || activePlan === "premium"}
              data-testid="checkout-premium-btn"
            >
              {checkingOut === "premium" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {activePlan === "premium" ? "Current Plan" : "Upgrade to Premium"}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* FAQ / Note */}
      <div className="text-center text-sm text-muted-foreground max-w-lg mx-auto">
        <p>All paid plans include a <strong>5-day free trial</strong> of Pro. Cancel anytime. Payments are securely processed by Stripe.</p>
      </div>
    </div>
  );
}
